import React, { useState, useEffect, useCallback } from "react";
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
  GridValueGetterParams,
  GridRenderCellParams,
  GridFilterModel,
  GridSortModel,
  GridFilterItem,
  useGridApiContext,
  useGridSelector,
  gridQuickFilterValuesSelector,
} from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  History as HistoryIcon,
  StickyNote2 as StickyNote2Icon,
} from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ConsultationList from "./Consultation/ConsultationList";

// Types

interface Patient {
  id: number;
  NumePrenume: string;
  CNP: number;
  DataNasterii: Date;
  LocalitateAdr: string;
  StradaAdr: string;
  NrAdr: string;
  BlocAdr: string;
  EtajAdr: string;
  ApartamentAdr: string;
  ScaraAdr: string;
  TelefonMobil: string;
  Email: string;
  Varsta: number;
  Observatii: string;
  isActive: boolean;
  rating: number;
  diagnostice: string[];
  alergii?: string;
}

interface SampleDataGridProps {
  title?: string;
  height?: number;
}
// Enhanced filter types
interface CustomFilter {
  field: string;
  operator: string;
  value: any;
  label?: string;
}

interface AdvancedFilters {
  searchTerm: string;
  ageMin: number | null;
  ageMax: number | null;
  locality: string;
  isActive: boolean | null;
  dateFrom: Date | null;
  dateTo: Date | null;
}

// Add API configuration constants
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const API_ENDPOINTS = {
  patients: "/Persoane/GetPagedPersons",
  createPatient: "/Persoane",
  updatePatient: "/Persoane",
  deletePatient: "/Persoane",
};

// Mock data generator
const generateMockData = (): Patient[] => {
  const departments = [
    "Engineering",
    "Marketing",
    "Sales",
    "HR",
    "Finance",
    "Operations",
  ];
  const positions = [
    "Manager",
    "Senior",
    "Junior",
    "Lead",
    "Director",
    "Specialist",
  ];
  const skills = [
    "React",
    "TypeScript",
    "Node.js",
    "Python",
    "SQL",
    "AWS",
    "Docker",
    "Kubernetes",
  ];
  const firstNames = [
    "Dorin",
    "Maria",
    "Ildiko",
    "Robert",
    "David",
    "Paul",
    "Alexandru",
    "Adina",
    "Ciprian",
    "Daniel",
  ];
  const lastNames = [
    "Popa",
    "Popescu",
    "Tamas",
    "Traica",
    "Murariu",
    "Hategan",
    "Fetico",
    "David",
    "Corpade",
    "Calinoiu",
  ];

  return Array.from({ length: 100 }, (_, index) => {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    /*const department =
      departments[Math.floor(Math.random() * departments.length)];
    const position = positions[Math.floor(Math.random() * positions.length)];*/

    return {
      id: index + 1,
      NumePrenume: `${firstName} ${lastName}`,
      CNP: Math.floor(Math.random() * 9000000000000) + 1000000000000,
      DataNasterii: new Date(
        1970 + Math.floor(Math.random() * 50),
        Math.floor(Math.random() * 12),
        Math.floor(Math.random() * 28) + 1,
      ),
      LocalitateAdr: `Localitatea ${Math.floor(Math.random() * 100) + 1}`,
      StradaAdr: `Strada ${Math.floor(Math.random() * 100)}-${
        Math.floor(Math.random() * 900) + 100
      }-${Math.floor(Math.random() * 9000) + 1000}`,
      TelefonMobil: `07${Math.floor(Math.random() * 900000000) + 100000000}`,
      Email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      Varsta:
        new Date().getFullYear() - (1970 + Math.floor(Math.random() * 50)),
      NrAdr: `${Math.floor(Math.random() * 200) + 1}`,
      BlocAdr: `B${Math.floor(Math.random() * 20) + 1}`,
      EtajAdr: `${Math.floor(Math.random() * 10) + 1}`,
      ApartamentAdr: `${Math.floor(Math.random() * 100) + 1}`,
      ScaraAdr: `${String.fromCharCode(65 + Math.floor(Math.random() * 26))}`,
      isActive: Math.random() > 0.1,
      rating: Math.floor(Math.random() * 5) + 1,
      diagnostice: ["Diabet", "Hipertensiune", "Astm"],
      Observatii: "Nici una",
      alergii: "Nici una",
    };
  });
};

const SampleDataGrid: React.FC<SampleDataGridProps> = ({
  title,
  height = 600,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Use translation for title if not provided
  const displayTitle = title || t("patients.title");

  // ✅ Add form state for dialog
  const [formData, setFormData] = useState<Partial<Patient>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [rows, setRows] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"view" | "edit" | "add">("view");
  const [selectedEmployee, setSelectedEmployee] = useState<Patient | null>(
    null,
  );
  const [consultationDialogOpen, setConsultationDialogOpen] = useState(false);
  const [selectedPatientForConsultation, setSelectedPatientForConsultation] =
    useState<Patient | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Add pagination state management
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Add row count state for server-side pagination
  const [rowCountState, setRowCountState] = useState(0);
  // ✅ Filter and sort models
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "NumePrenume", sort: "asc" },
  ]);

  // ✅ Advanced filters state
  //const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    searchTerm: "",
    ageMin: null,
    ageMax: null,
    locality: "",
    isActive: null,
    dateFrom: null,
    dateTo: null,
  });

  // ✅ Add state for quick filter value
  const [quickFilterValues, setQuickFilterValues] = useState<string[]>([]);

  // Function to transform API data to Patient interface
  const transformApiDataToPatient = useCallback((apiData: any): Patient => {
    return {
      id: apiData.id || apiData.patientId || 0,
      NumePrenume:
        apiData.numePrenume ||
        apiData.fullName ||
        `${apiData.firstName || ""} ${apiData.lastName || ""}`.trim(),
      CNP: apiData.CNP || apiData.cnp || apiData.personalNumber || "",
      DataNasterii: new Date(
        apiData.dataNasterii ||
          apiData.birthDate ||
          apiData.dateOfBirth ||
          new Date(),
      ),
      LocalitateAdr:
        apiData.localitateAdr || apiData.city || apiData.locality || "",
      StradaAdr:
        apiData.stradaAdr || apiData.street || apiData.address?.street || "",
      NrAdr:
        apiData.nrAdr || apiData.streetNumber || apiData.address?.number || "",
      BlocAdr:
        apiData.blocAdr || apiData.building || apiData.address?.building || "",
      EtajAdr: apiData.EtajAdr || apiData.floor || apiData.address?.floor || "",
      ApartamentAdr:
        apiData.apartamentAdr ||
        apiData.apartment ||
        apiData.address?.apartment ||
        "",
      ScaraAdr:
        apiData.scaraAdr ||
        apiData.staircase ||
        apiData.address?.staircase ||
        "",
      TelefonMobil:
        apiData.telefonMobil ||
        apiData.phone ||
        apiData.mobile ||
        apiData.phoneNumber ||
        "",
      Email: apiData.email || apiData.email || apiData.emailAddress || "",
      Varsta: apiData.varsta || apiData.age,
      isActive: apiData.isActive !== undefined ? apiData.isActive : true,
      rating: apiData.rating || apiData.score || 1,
      diagnostice:
        apiData.diagnostice || apiData.diagnoses || apiData.conditions || [],
      Observatii: apiData.Observatii || apiData.notes || "",
      alergii: apiData.alergii || apiData.allergies || "",
    };
  }, []);

  // Updated loadData function to call web API
  const loadData = useCallback(
    async (
      pageToLoad?: number,
      pageSizeToLoad?: number,
      filtersToApply?: GridFilterModel,
      sortToApply?: GridSortModel,
      advancedFiltersToApply?: AdvancedFilters,
      quickFilterToApply?: string[], // ✅ Accept quick filter array
    ) => {
      try {
        setLoading(true);
        setError(null);
        // Use passed parameters or current state
        const currentPage =
          pageToLoad !== undefined ? pageToLoad : paginationModel.page;
        const currentPageSize =
          pageSizeToLoad !== undefined
            ? pageSizeToLoad
            : paginationModel.pageSize;
        const currentFilters = filtersToApply || filterModel;
        const currentSort = sortToApply || sortModel;
        const currentAdvancedFilters =
          advancedFiltersToApply || advancedFilters;
        const currentQuickFilter = quickFilterToApply || quickFilterValues;
        console.log(
          `🌐 Loading patients from API - Page: ${currentPage + 1}, Size: ${
            currentPageSize ? currentPageSize : 10
          }`,
        );
        // ✅ Build filter object for API
        let apiFilters: any = {
          //isActive: currentAdvancedFilters.isActive,
        };
        // Add DataGrid filters
        currentFilters.items.forEach((filter: GridFilterItem) => {
          if (
            filter.value !== undefined &&
            filter.value !== null &&
            filter.value !== ""
          ) {
            apiFilters = {
              operatorType: filter.operator,
              field: filter.field,
              value: filter.value,
            };
          }
        });
        // Add advanced filters
        if (currentAdvancedFilters.searchTerm) {
          apiFilters.search = currentAdvancedFilters.searchTerm;
        }
        if (currentAdvancedFilters.ageMin !== null) {
          apiFilters.ageMin = currentAdvancedFilters.ageMin;
        }
        if (currentAdvancedFilters.ageMax !== null) {
          apiFilters.ageMax = currentAdvancedFilters.ageMax;
        }
        if (currentAdvancedFilters.locality) {
          apiFilters.locality = currentAdvancedFilters.locality;
        }
        if (currentAdvancedFilters.dateFrom) {
          apiFilters.dateFrom = currentAdvancedFilters.dateFrom.toISOString();
        }
        if (currentAdvancedFilters.dateTo) {
          apiFilters.dateTo = currentAdvancedFilters.dateTo.toISOString();
        }

        // ✅ Add quick filter to API filters
        if (currentQuickFilter.length > 0) {
          apiFilters.quickSearch = currentQuickFilter.join(" "); // Join multiple terms
        }
        // ✅ Build sort object for API
        const apiSort =
          currentSort.length > 0
            ? {
                field: currentSort[0].field,
                direction: currentSort[0].sort || "asc",
              }
            : {
                field: "NumePrenume",
                direction: "asc",
              };

        const requestData = {
          page: currentPage + 1,
          limit: currentPageSize,
          offset: currentPage * currentPageSize,
          sortBy: apiSort.field,
          sortDirection: apiSort.direction,
          filters: apiFilters,
          quickFilterValue: currentQuickFilter,
        };

        console.log(
          "🔍 API Request with filters and sort:",
          JSON.stringify(requestData),
        );

        // Configure axios request
        const authToken = localStorage.getItem("authToken");
        console.log("🔑 Auth Token:", authToken || "NOT_SET");
        const config = {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            // Add authentication if needed
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            // Add API key if needed
            "X-API-Key": process.env.REACT_APP_API_KEY,
          },
        };

        // Make API call
        const response = await axios.post(
          `${API_BASE_URL}${API_ENDPOINTS.patients}`,
          requestData,
          config,
        );

        console.log("✅ API Response received:", {
          status: response.status,
          dataLength: response.data?.data?.length || 0,
          totalCount: response.data?.totalCount || 0,
          currentPage: response.data?.currentPage || currentPage + 1,
        });
        // Process the response data
        let patients: Patient[] = [];
        let totalCount = 0;

        if (response.data) {
          if (Array.isArray(response.data)) {
            // If response.data is directly an array
            patients = response.data.map(transformApiDataToPatient);
            totalCount = patients.length;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            // If response.data has a data property with the array
            patients = response.data.data.map(transformApiDataToPatient);
            totalCount =
              response.data.totalCount ||
              response.data.total ||
              patients.length;
          }
        }

        console.log(
          "📊 Processed patients:",
          patients.length,
          "Total:",
          totalCount,
          "Current Page:",
          currentPage + 1,
          "Page Size:",
          currentPageSize,
        );
        setRows(patients);
        setRowCountState(totalCount);

        // ✅ Update pagination model only if parameters were passed
        if (pageToLoad !== undefined || pageSizeToLoad !== undefined) {
          setPaginationModel({
            page: currentPage,
            pageSize: currentPageSize,
          });
        }
      } catch (err) {
        console.error("❌ Error loading patients:", err);

        let errorMessage = "Failed to load patient data";

        if (axios.isAxiosError(err)) {
          if (err.code === "ECONNABORTED") {
            errorMessage = "Request timeout - please try again";
          } else if (err.response) {
            // Server responded with error status
            switch (err.response.status) {
              case 401:
                errorMessage = "Unauthorized - please login again";
                break;
              case 403:
                errorMessage = "Access forbidden - insufficient permissions";
                break;
              case 404:
                errorMessage = "Patients API endpoint not found";
                break;
              case 500:
                errorMessage = "Server error - please try again later";
                break;
              default:
                errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
            }
          } else if (err.request) {
            // Network error
            errorMessage = "Network error - please check your connection";
          }
        }

        setError(errorMessage);

        // Fallback to mock data in development
        if (process.env.NODE_ENV === "development") {
          console.log("🔄 Falling back to mock data in development mode");
          let mockData = generateMockData();
          // Apply quick filter missing from mock data
          // Apply advanced filters
          if (advancedFilters.searchTerm) {
            const searchTerm = advancedFilters.searchTerm.toLowerCase();
            mockData = mockData.filter(
              (patient) =>
                patient.NumePrenume.toLowerCase().includes(searchTerm) ||
                patient.Email.toLowerCase().includes(searchTerm),
            );
          }

          if (advancedFilters.ageMin !== null) {
            mockData = mockData.filter(
              (patient) => patient.Varsta >= advancedFilters.ageMin!,
            );
          }

          if (advancedFilters.ageMax !== null) {
            mockData = mockData.filter(
              (patient) => patient.Varsta <= advancedFilters.ageMax!,
            );
          }

          if (advancedFilters.locality) {
            mockData = mockData.filter((patient) =>
              patient.LocalitateAdr.toLowerCase().includes(
                advancedFilters.locality.toLowerCase(),
              ),
            );
          }

          if (advancedFilters.isActive !== null) {
            mockData = mockData.filter(
              (patient) => patient.isActive === advancedFilters.isActive,
            );
          }

          // Apply sorting
          if (sortModel.length > 0) {
            const sortField = sortModel[0].field as keyof Patient;
            const sortDirection = sortModel[0].sort;

            mockData.sort((a, b) => {
              const aValue = a[sortField];
              const bValue = b[sortField];

              // Handle undefined/null values
              if (aValue == null && bValue == null) return 0;
              if (aValue == null) return sortDirection === "asc" ? 1 : -1;
              if (bValue == null) return sortDirection === "asc" ? -1 : 1;

              if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
              if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
              return 0;
            });
          }
          // Apply pagination
          const startIndex = paginationModel.page * paginationModel.pageSize;
          const endIndex = startIndex + paginationModel.pageSize;
          const pageData = mockData.slice(startIndex, endIndex);

          setRows(pageData);
          setRowCountState(mockData.length);
          setError("Using mock data with filters - API unavailable");
        }
      } finally {
        setLoading(false);
      }
    },
    [
      paginationModel.page,
      paginationModel.pageSize,
      transformApiDataToPatient,
      filterModel,
      sortModel,
      advancedFilters,
      quickFilterValues,
    ],
  );

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  // ✅ Fixed pagination change handler
  const handlePaginationModelChange = useCallback(
    (newPaginationModel: typeof paginationModel) => {
      console.log("📄 Pagination changed:", newPaginationModel);

      const pageChanged = newPaginationModel.page !== paginationModel.page;
      const pageSizeChanged =
        newPaginationModel.pageSize !== paginationModel.pageSize;

      if (pageChanged || pageSizeChanged) {
        setPaginationModel(newPaginationModel);
        loadData(
          newPaginationModel.page,
          newPaginationModel.pageSize,
          filterModel,
          sortModel,
          advancedFilters,
          filterModel.quickFilterValues,
        );
      }
    },
    [filterModel, sortModel, advancedFilters, loadData],
  );

  // Add refresh function
  const handleRefresh = useCallback(() => {
    console.log("🔄 Refreshing patient data...");
    loadData();
  }, [loadData]);

  // ✅ Sort change handler
  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      console.log("↕️ Sort changed:", newSortModel);
      setSortModel(newSortModel);
      loadData(
        paginationModel.page,
        paginationModel.pageSize,
        filterModel,
        newSortModel,
        advancedFilters,
        filterModel.quickFilterValues,
      );
    },
    [paginationModel, filterModel, advancedFilters, loadData],
  );

  // ✅ Update other handlers to preserve quick filter
  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      console.log("🔍 Filter changed:", newFilterModel);
      console.log("🔍 Quick Filter changed:", newFilterModel.quickFilterValues);
      setFilterModel(newFilterModel);

      const newPaginationModel = { ...paginationModel, page: 0 };
      setPaginationModel(newPaginationModel);
      setQuickFilterValues(newFilterModel.quickFilterValues || []);
      loadData(
        0,
        paginationModel.pageSize,
        newFilterModel,
        sortModel,
        advancedFilters,
        newFilterModel.quickFilterValues,
      );
    },
    [paginationModel, sortModel, advancedFilters, loadData],
  );

  // ✅ Advanced filters change handler
  const handleAdvancedFiltersChange = useCallback(
    (newAdvancedFilters: AdvancedFilters) => {
      console.log("🔍 Advanced filters changed:", newAdvancedFilters);
      setAdvancedFilters(newAdvancedFilters);

      // Reset to first page when filtering
      const newPaginationModel = { ...paginationModel, page: 0 };
      setPaginationModel(newPaginationModel);

      loadData(
        0,
        paginationModel.pageSize,
        filterModel,
        sortModel,
        newAdvancedFilters,
      );
    },
    [paginationModel, filterModel, sortModel, loadData],
  );

  // ✅ Add handler for quick filter changes
  const handleQuickFilterChange = useCallback(
    (quickFilterValues: string[]) => {
      console.log("⚡ Quick filter changed:", quickFilterValues);
      setQuickFilterValues(quickFilterValues);

      // Reset to first page when quick filtering
      const newPaginationModel = { ...paginationModel, page: 0 };
      setPaginationModel(newPaginationModel);

      // Load data with new quick filter
      loadData(
        0,
        paginationModel.pageSize,
        filterModel,
        sortModel,
        advancedFilters,
        quickFilterValues,
      );
    },
    [paginationModel, filterModel, sortModel, advancedFilters, loadData],
  );

  // ✅ Clear all filters
  // ✅ Update clear all filters to include quick filter
  const handleClearAllFilters = useCallback(() => {
    console.log("🧹 Clearing all filters including quick filter");
    setFilterModel({ items: [] });
    setAdvancedFilters({
      searchTerm: "",
      ageMin: null,
      ageMax: null,
      locality: "",
      isActive: null,
      dateFrom: null,
      dateTo: null,
    });
    setQuickFilterValues([]); // ✅ Clear quick filter

    const newPaginationModel = { ...paginationModel, page: 0 };
    setPaginationModel(newPaginationModel);

    loadData(
      0,
      paginationModel.pageSize,
      { items: [] },
      sortModel,
      {
        searchTerm: "",
        ageMin: null,
        ageMax: null,
        locality: "",
        isActive: null,
        dateFrom: null,
        dateTo: null,
      },
      [],
    ); // ✅ Pass empty quick filter array
  }, [paginationModel, sortModel, loadData]);

  // Action handlers
  const handleDelete = useCallback(
    async (patient: Patient) => {
      console.log("🗑️ Deleting patient:", patient.id);
      if (
        !window.confirm(
          `Are you sure you want to delete ${patient.NumePrenume}?`,
        )
      ) {
        return;
      }

      try {
        setLoading(true);

        // Call delete API
        await axios.delete(
          `${API_BASE_URL}${API_ENDPOINTS.deletePatient}/${patient.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          },
        );

        console.log("✅ Patient deleted successfully");

        // Remove from local state
        setRows((prev) => prev.filter((row) => row.id !== patient.id));

        // Or refresh from server
        // await loadData();
      } catch (err) {
        // Enhanced error logging
        if (axios.isAxiosError(err)) {
          console.log("🌐 Axios error details:", {
            message: err.message,
            code: err.code,
            hasResponse: !!err.response,
            hasRequest: !!err.request,
            configUrl: err.config?.url,
            configMethod: err.config?.method,
            configTimeout: err.config?.timeout,
          });

          if (err.response) {
            console.log("📡 Server response error:", {
              status: err.response.status,
              statusText: err.response.statusText,
              headers: err.response.headers,
              data: err.response.data,
              dataType: typeof err.response.data,
            });
          } else if (err.request) {
            console.log("📡 Network request error:", {
              request: err.request,
              readyState: err.request.readyState,
              status: err.request.status,
              statusText: err.request.statusText,
            });
          }
        } else {
          console.log("❓ Non-axios error:", {
            message: err instanceof Error ? err.message : "Unknown error",
            stack: err instanceof Error ? err.stack : "No stack trace",
            type: typeof err,
            constructor: err?.constructor?.name,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [loadData],
  );

  const handleToggleRating = useCallback((id: GridRowId) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, rating: row.rating === 5 ? 1 : 5 } : row,
      ),
    );
  }, []);

  // ✅ Enhanced handleSave function with comprehensive logging
  const handleSave = useCallback(
    async (isEdit: boolean = false) => {
      console.log("🚀 handleSave started", {
        isEdit,
        dialogMode,
        formDataKeys: Object.keys(formData),
        formDataId: formData.id,
      });

      try {
        setLoading(true);
        setFormErrors({});

        console.log("📝 Form data received:", {
          ...formData,
          DataNasterii: formData.DataNasterii
            ? new Date(formData.DataNasterii).toISOString()
            : null,
        });

        // ✅ Validate form data
        console.log("🔍 Starting form validation...");
        const errors: Record<string, string> = {};

        if (!formData.NumePrenume?.trim()) {
          console.log("❌ Validation error: NumePrenume is missing");
          errors.NumePrenume = t("patients.validation.fullNameRequired");
        } else {
          console.log(
            "✅ NumePrenume validation passed:",
            formData.NumePrenume,
          );
        }

        if (!formData.CNP || String(formData.CNP).length !== 13) {
          console.log("❌ Validation error: CNP length invalid", {
            cnp: formData.CNP,
            length: formData.CNP ? String(formData.CNP).length : 0,
          });
          errors.CNP = t("patients.validation.invalidCNP");
        } else {
          console.log("✅ CNP validation passed:", formData.CNP);
        }

        if (!formData.Email?.trim()) {
          console.log("❌ Validation error: Email is missing");
          errors.Email = t("patients.validation.emailRequired");
        } else if (!/\S+@\S+\.\S+/.test(formData.Email)) {
          console.log(
            "❌ Validation error: Email format invalid",
            formData.Email,
          );
          errors.Email = t("patients.validation.invalidEmail");
        } else {
          console.log("✅ Email validation passed:", formData.Email);
        }

        if (!formData.TelefonMobil?.trim()) {
          console.log("❌ Validation error: TelefonMobil is missing");
          errors.TelefonMobil = t("patients.validation.phoneRequired");
        } else {
          console.log(
            "✅ TelefonMobil validation passed:",
            formData.TelefonMobil,
          );
        }

        console.log("📊 Validation results:", {
          totalErrors: Object.keys(errors).length,
          errors: errors,
        });

        if (Object.keys(errors).length > 0) {
          setFormErrors(errors);
          console.log("❌ Validation failed, stopping save process");
          return;
        }

        console.log("✅ All validations passed, preparing patient data...");

        // ✅ Prepare patient data for API
        const patientData: Partial<Patient> = {
          ...formData,
          // Ensure proper data types
          Varsta: formData.Varsta ? Number(formData.Varsta) : 0,
          DataNasterii: formData.DataNasterii
            ? new Date(formData.DataNasterii)
            : new Date(),
          isActive: formData.isActive !== undefined ? formData.isActive : true,
          rating: formData.rating || 1,
          diagnostice: formData.diagnostice || [],
        };

        console.log("📦 Prepared patient data for API:", {
          ...patientData,
          DataNasterii: patientData.DataNasterii?.toISOString(),
          dataSize: JSON.stringify(patientData).length,
        });

        console.log(
          `${isEdit ? "✏️ Updating" : "➕ Creating"} patient with ID:`,
          patientData.id || "NEW",
        );

        // Prepare request configuration
        const authToken = localStorage.getItem("authToken");
        const apiKey = process.env.REACT_APP_API_KEY;

        console.log("🔐 Authentication details:", {
          hasAuthToken: !!authToken,
          authTokenLength: authToken?.length || 0,
          hasApiKey: !!apiKey,
          apiKeyLength: apiKey?.length || 0,
        });

        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-API-Key": apiKey,
          },
          timeout: 10000,
        };

        console.log("🔧 Request config prepared:", {
          timeout: config.timeout,
          headers: {
            ...config.headers,
            Authorization: config.headers.Authorization
              ? "[HIDDEN]"
              : "NOT_SET",
            "X-API-Key": config.headers["X-API-Key"] ? "[HIDDEN]" : "NOT_SET",
          },
        });

        let response;
        let apiUrl;

        if (isEdit && patientData.id) {
          // ✅ Update existing patient
          apiUrl = `${API_BASE_URL}${API_ENDPOINTS.updatePatient}/${patientData.id}`;
          console.log("🔄 Making PUT request to update patient:", {
            url: apiUrl,
            patientId: patientData.id,
            method: "PUT",
          });

          console.log("📤 Sending PUT request with data:", {
            url: apiUrl,
            dataKeys: Object.keys(patientData),
            payloadSize: JSON.stringify(patientData).length + " bytes",
          });

          response = await axios.put(apiUrl, patientData, config);

          console.log("✅ PUT request successful:", {
            status: response.status,
            statusText: response.statusText,
            responseDataKeys: response.data ? Object.keys(response.data) : [],
            responseSize: JSON.stringify(response.data).length + " bytes",
          });
        } else {
          // ✅ Create new patient
          apiUrl = `${API_BASE_URL}${API_ENDPOINTS.createPatient}`;
          console.log("🆕 Making POST request to create patient:", {
            url: apiUrl,
            method: "POST",
          });

          console.log("📤 Sending POST request with data:", {
            url: apiUrl,
            dataKeys: Object.keys(patientData),
            payloadSize: JSON.stringify(patientData).length + " bytes",
          });

          response = await axios.post(apiUrl, patientData, config);

          console.log("✅ POST request successful:", {
            status: response.status,
            statusText: response.statusText,
            responseDataKeys: response.data ? Object.keys(response.data) : [],
            responseSize: JSON.stringify(response.data).length + " bytes",
            newPatientId: response.data?.id || "NOT_PROVIDED",
          });
        }

        console.log("📨 Full API response:", {
          status: response.status,
          statusText: response.statusText,
          headers: Object.keys(response.headers),
          data: response.data,
        });

        // ✅ Show success message
        console.log("🧹 Clearing previous errors...");
        setError(null);

        // ✅ Refresh data from server
        console.log("🔄 Refreshing data from server...");
        await loadData();
        console.log("✅ Data refresh completed");

        // ✅ Close dialog and reset form
        console.log("🚪 Closing dialog and resetting form...");
        setDialogOpen(false);
        setFormData({});
        setFormErrors({});

        console.log(
          `🎉 Patient ${isEdit ? "updated" : "created"} successfully!`,
          {
            operation: isEdit ? "UPDATE" : "CREATE",
            patientId: patientData.id || response.data?.id,
            timestamp: new Date().toISOString(),
          },
        );
      } catch (err) {
        console.error(
          `💥 Error ${isEdit ? "updating" : "creating"} patient:`,
          err,
        );

        // Enhanced error logging
        if (axios.isAxiosError(err)) {
          console.log("🌐 Axios error details:", {
            message: err.message,
            code: err.code,
            hasResponse: !!err.response,
            hasRequest: !!err.request,
            configUrl: err.config?.url,
            configMethod: err.config?.method,
            configTimeout: err.config?.timeout,
          });

          if (err.response) {
            console.log("📡 Server response error:", {
              status: err.response.status,
              statusText: err.response.statusText,
              headers: err.response.headers,
              data: err.response.data,
              dataType: typeof err.response.data,
            });
          } else if (err.request) {
            console.log("📡 Network request error:", {
              request: err.request,
              readyState: err.request.readyState,
              status: err.request.status,
              statusText: err.request.statusText,
            });
          }
        } else {
          console.log("❓ Non-axios error:", {
            message: err instanceof Error ? err.message : "Unknown error",
            stack: err instanceof Error ? err.stack : "No stack trace",
            type: typeof err,
            constructor: err?.constructor?.name,
          });
        }

        let errorMessage = `Failed to ${isEdit ? "update" : "create"} patient`;

        if (axios.isAxiosError(err)) {
          if (err.response) {
            console.log(`🔍 Handling HTTP ${err.response.status} error...`);
            switch (err.response.status) {
              case 400:
                errorMessage = "Invalid patient data provided";
                console.log(
                  "🔧 400 Error - checking for field-specific errors:",
                  err.response.data,
                );
                // Handle field-specific errors from server
                if (err.response.data?.errors) {
                  console.log(
                    "📝 Setting field-specific errors:",
                    err.response.data.errors,
                  );
                  setFormErrors(err.response.data.errors);
                }
                break;
              case 401:
                errorMessage = "Unauthorized - please login again";
                console.log("🔐 401 Error - authentication required");
                break;
              case 403:
                errorMessage = "Access forbidden - insufficient permissions";
                console.log("🚫 403 Error - insufficient permissions");
                break;
              case 404:
                errorMessage = isEdit
                  ? "Patient not found"
                  : "API endpoint not found";
                console.log("🔍 404 Error - resource not found");
                break;
              case 409:
                errorMessage = "Patient with this CNP already exists";
                console.log("⚠️ 409 Error - conflict (duplicate CNP)");
                break;
              case 422:
                errorMessage = "Validation failed - please check your data";
                console.log("✏️ 422 Error - validation failed on server");
                break;
              case 500:
                errorMessage = "Server error - please try again later";
                console.log("🔥 500 Error - internal server error");
                break;
              default:
                errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
                console.log(
                  `❓ ${err.response.status} Error - unhandled status code`,
                );
            }
          } else if (err.request) {
            errorMessage = "Network error - please check your connection";
            console.log("🌐 Network error - no response received");
          }
        }

        console.log("📢 Setting error message for user:", errorMessage);
        setError(errorMessage);
      } finally {
        console.log("🏁 handleSave cleanup - setting loading to false");
        setLoading(false);
        console.log("✅ handleSave completed");
      }
    },
    [formData, loadData, dialogMode],
  );

  // ✅ Add function to calculate age from birth date
  const calculateAge = useCallback((birthDate: string | Date): number => {
    if (!birthDate) return 0;

    const birth = new Date(birthDate);
    const today = new Date();

    // Check if birth date is valid
    if (isNaN(birth.getTime())) {
      console.log("Invalid birth date:", birthDate);
      return 0;
    }

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    // If birthday hasn't occurred this year, subtract 1
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    // Ensure age is not negative
    return Math.max(0, age);
  }, []);

  // ✅ Enhanced form field change handler
  const handleFormFieldChange = useCallback(
    (field: keyof Patient, value: any) => {
      console.log(`📝 Form field changed: ${field} = ${value}`);

      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [field]: value,
        };

        // ✅ Auto-calculate age when birth date changes
        if (field === "DataNasterii" && value) {
          const calculatedAge = calculateAge(value);
          console.log(
            `🎂 Calculated age from birth date ${value}: ${calculatedAge} years`,
          );
          newFormData.Varsta = calculatedAge;
        }

        return newFormData;
      });

      // Clear field error when user starts typing
      if (formErrors[field]) {
        setFormErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }

      // ✅ Also clear age error if birth date is being corrected
      if (field === "DataNasterii" && formErrors.Varsta) {
        setFormErrors((prev) => ({
          ...prev,
          Varsta: "",
        }));
      }
    },
    [formErrors, calculateAge],
  );

  // ✅ Enhanced dialog handlers with age calculation
  const handleView = useCallback((patient: Patient) => {
    console.log("👁️ Viewing patient:", patient.id);
    setSelectedEmployee(patient);
    setFormData(patient);
    setDialogMode("view");
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback(
    (patient: Patient) => {
      console.log("✏️ Editing patient:", patient.id);
      setSelectedEmployee(patient);

      // ✅ Recalculate age when editing to ensure consistency
      const patientWithCalculatedAge = {
        ...patient,
        Varsta: patient.DataNasterii
          ? calculateAge(patient.DataNasterii)
          : patient.Varsta,
      };

      setFormData(patientWithCalculatedAge);
      setDialogMode("edit");
      setDialogOpen(true);
    },
    [calculateAge],
  );

  const handleAdd = useCallback(() => {
    console.log("➕ Adding new patient");
    setSelectedEmployee(null);
    setFormData({
      NumePrenume: "",
      CNP: 0,
      Email: "",
      TelefonMobil: "",
      LocalitateAdr: "",
      StradaAdr: "",
      NrAdr: "",
      BlocAdr: "",
      EtajAdr: "",
      ApartamentAdr: "",
      ScaraAdr: "",
      alergii: "",
      Observatii: "",
      isActive: true,
      rating: 1,
      diagnostice: [],
      Varsta: 0, // ✅ Initialize age to 0
    });
    setFormErrors({});
    setDialogMode("add");
    setDialogOpen(true);
  }, []);

  // ✅ Enhanced dialog close handler
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSelectedEmployee(null);
    setFormData({});
    setFormErrors({});
  }, []);

  // Display consultation list for the selected patient
  const handleConsultations = useCallback((patient: Patient) => {
    console.log(
      "📋 Opening consultations for patient:",
      patient.id,
      patient.NumePrenume,
    );
    setSelectedPatientForConsultation(patient);
    setConsultationDialogOpen(true);
  }, []);

  const handleCloseConsultations = useCallback(() => {
    setConsultationDialogOpen(false);
    setSelectedPatientForConsultation(null);
  }, []);

  // Column definitions
  const columns: GridColDef[] = [
    /*{
      field: "avatar",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Avatar
          src={params.row.avatar}
          alt={`${params.row.firstName} ${params.row.lastName}`}
          sx={{ width: 32, height: 32 }}
        >
          {params.row.firstName[0]}
        </Avatar>
      ),
    },*/
    {
      field: "NumePrenume",
      headerName: "Numele complet",
      width: 220,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.NumePrenume || ""} `,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.CNP}
            </Typography>
          </Box>
          {params.row.alergii && (
            <Tooltip
              title={
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Alergii:
                  </Typography>
                  <Typography variant="body2">{params.row.alergii}</Typography>
                </Box>
              }
              arrow
              placement="right"
            >
              <StickyNote2Icon
                sx={{
                  fontSize: 20,
                  color: "warning.main",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "Varsta",
      headerName: "Varsta",
      sortable: false,
      width: 50,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "TelefonMobil",
      headerName: "Telefon",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "Email",
      headerName: "Email",
      width: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography
            variant="body2"
            component="a"
            href={`mailto:${params.value}`}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "LocalitateAdr",
      headerName: "Localitatea",
      sortable: true,
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "StradaAdr",
      headerName: "Adresa",
      width: 300,
      valueGetter: (params: GridValueGetterParams) =>
        `Strada: ${params.row.StradaAdr || ""} Nr. ${
          params.row.NrAdr || ""
        }, bl. ${params.row.BlAdr || ""}`,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`Sc: ${params.row.ScaraAdr || ""} Et. ${
              params.row.EtajAdr || ""
            }, ap. ${params.row.ApartamentAdr || ""}`}
          </Typography>
        </Box>
      ),
    },
    /*{
      field: "salary",
      headerName: "Salary",
      type: "number",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={params.value > 100000 ? "success.main" : "text.primary"}
        >
          ${params.value?.toLocaleString()}
        </Typography>
      ),
    },*/
    {
      field: "DataNasterii",
      headerName: "Data Nasterii",
      type: "date",
      width: 120,
      valueGetter: (params: GridValueGetterParams) => new Date(params.value),
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontFamily="monospace">
          {new Date(params.value).toLocaleDateString("ro-RO", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </Typography>
      ),
    },
    /*{
      field: "isActive",
      headerName: "Status",
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "default"}
          size="small"
          variant={params.value ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "rating",
      headerName: "Rating",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {Array.from({ length: 5 }, (_, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={() => handleToggleRating(params.id)}
              sx={{
                p: 0,
                color: index < params.value ? "warning.main" : "grey.300",
              }}
            >
              {index < params.value ? (
                <StarIcon sx={{ fontSize: 16 }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          ))}
        </Box>
      ),
    },
    {
      field: "skills",
      headerName: "Skills",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {params.value?.slice(0, 2).map((skill: string, index: number) => (
            <Chip
              key={index}
              label={skill}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.6875rem", height: 20 }}
            />
          ))}
          {params.value?.length > 2 && (
            <Chip
              label={`+${params.value.length - 2}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.6875rem", height: 20 }}
            />
          )}
        </Box>
      ),
    },*/
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,

      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="consultations"
          icon={
            <Tooltip title="Consultation History">
              <HistoryIcon />
            </Tooltip>
          }
          label="Consultations"
          onClick={() => handleConsultations(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          key="view"
          icon={
            <Tooltip title="View Details">
              <ViewIcon />
            </Tooltip>
          }
          label="View"
          onClick={() => handleView(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          key="edit"
          icon={
            <Tooltip title="Edit Patient">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handleEdit(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          key="delete"
          icon={
            <Tooltip title="Delete Patient">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDelete(params.row)}
          color="error"
        />,
      ],
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {displayTitle}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          {t("patients.addPatient")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* ✅ Quick filter status indicator */}
      {filterModel &&
        filterModel.quickFilterValues &&
        filterModel.quickFilterValues.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setQuickFilterValues([]);
                  loadData(
                    0,
                    paginationModel.pageSize,
                    filterModel,
                    sortModel,
                    advancedFilters,
                    [],
                  );
                }}
              >
                Clear Quick Filter
              </Button>
            }
          >
            Quick search active: "{filterModel.quickFilterValues.join(", ")}" •{" "}
            {rowCountState} results found
          </Alert>
        )}
      {/* Selected rows info */}
      {selectedRows.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setSelectedRows([])}
            >
              Clear Selection
            </Button>
          }
        >
          {selectedRows.length} patient(s) selected
        </Alert>
      )}

      {/* Data Grid */}
      <Paper
        elevation={3}
        sx={{
          height,
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-main": {
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              },
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            "& .MuiDataGrid-columnHeader": {
              fontWeight: 600,
              fontSize: "0.875rem",
            },
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: alpha(theme.palette.grey[50], 0.5),
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          // ✅ Pagination Configuration
          pagination
          paginationMode="server" // or "server" for server-side pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          // ✅ Row count (important for server-side pagination)
          rowCount={rowCountState}
          // ✅ Filtering Configuration
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          // ✅ Sorting Configuration
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={setSelectedRows}
          rowSelectionModel={selectedRows}
          slots={{
            toolbar: GridToolbar,
            loadingOverlay: LinearProgress,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 800,
                placeholder: "Search patients...",
              },
            },
          }}
          // ✅ Add quick filter change handler
          //onStateChange={(state) => {
          //  const newQuickFilterValues = state.filter?.quickFilterValues || [];
          //  if (
          //    JSON.stringify(newQuickFilterValues) !==
          //    JSON.stringify(quickFilterValues)
          //  ) {
          //    handleQuickFilterChange(newQuickFilterValues);
          //  }
          //}}
          initialState={{
            sorting: {
              sortModel: [{ field: "fullName", sort: "asc" }],
            },
          }}
          density="comfortable"
        />
      </Paper>

      {/* Dialog for viewing/editing */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            fontWeight: 600,
            fontSize: "1.25rem",
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          {dialogMode === "view" && "Detalii Pacient"}
          {dialogMode === "edit" && "Editare Pacient"}
          {dialogMode === "add" && "Adăugare Pacient Nou"}
        </DialogTitle>

        <DialogContent sx={{ pt: 3, marginTop: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Nume Prenume *"
              value={formData.NumePrenume || ""}
              onChange={(e) =>
                handleFormFieldChange("NumePrenume", e.target.value)
              }
              disabled={dialogMode === "view"}
              fullWidth
              variant="outlined"
              error={!!formErrors.NumePrenume}
              helperText={formErrors.NumePrenume}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="CNP *"
                value={formData.CNP !== undefined ? String(formData.CNP) : ""}
                onChange={(e) => {
                  // Keep only digits, trim to max 13 characters
                  const digitsOnly = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 13);
                  // Update form value
                  handleFormFieldChange("CNP", digitsOnly);
                  // Immediate length validation
                  if (digitsOnly.length !== 13) {
                    setFormErrors((prev) => ({
                      ...prev,
                      CNP: t("patients.validation.invalidCNP"),
                    }));
                  } else {
                    setFormErrors((prev) => ({ ...prev, CNP: "" }));
                  }
                }}
                onBlur={() => {
                  const value =
                    formData.CNP !== undefined ? String(formData.CNP) : "";
                  if (value.length !== 13) {
                    setFormErrors((prev) => ({
                      ...prev,
                      CNP: t("patients.validation.invalidCNP"),
                    }));
                  } else {
                    setFormErrors((prev) => ({ ...prev, CNP: "" }));
                  }
                }}
                disabled={dialogMode === "view"}
                fullWidth
                variant="outlined"
                inputProps={{
                  maxLength: 13,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                error={!!formErrors.CNP}
                helperText={
                  formErrors.CNP
                    ? formErrors.CNP
                    : `${
                        formData.CNP !== undefined
                          ? String(formData.CNP).length
                          : 0
                      }/13`
                }
              />

              <TextField
                label="Email *"
                type="email"
                value={formData.Email || ""}
                onChange={(e) => {
                  const email = e.target.value;
                  handleFormFieldChange("Email", email);

                  // Validate email format as user types
                  if (!email.trim()) {
                    setFormErrors((prev) => ({
                      ...prev,
                      Email: t("patients.validation.emailRequired"),
                    }));
                    return;
                  }
                  const emailRegex = /^\S+@\S+\.\S+$/;
                  if (!emailRegex.test(email)) {
                    setFormErrors((prev) => ({
                      ...prev,
                      Email: t("patients.validation.invalidEmail"),
                    }));
                  } else {
                    setFormErrors((prev) => ({ ...prev, Email: "" }));
                  }
                }}
                onBlur={() => {
                  const email = (formData.Email || "").trim();
                  const emailRegex = /^\S+@\S+\.\S+$/;
                  if (!email) {
                    setFormErrors((prev) => ({
                      ...prev,
                      Email: t("patients.validation.emailRequired"),
                    }));
                  } else if (!emailRegex.test(email)) {
                    setFormErrors((prev) => ({
                      ...prev,
                      Email: t("patients.validation.invalidEmail"),
                    }));
                  } else {
                    // normalize to lowercase
                    handleFormFieldChange("Email", email.toLowerCase());
                    setFormErrors((prev) => ({ ...prev, Email: "" }));
                  }
                }}
                disabled={dialogMode === "view"}
                fullWidth
                variant="outlined"
                error={!!formErrors.Email}
                helperText={formErrors.Email || "Ex: nume@exemplu.com"}
              />

              <TextField
                label="Telefon Mobil *"
                value={formData.TelefonMobil || ""}
                onChange={(e) =>
                  handleFormFieldChange("TelefonMobil", e.target.value)
                }
                disabled={dialogMode === "view"}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 15 }}
                error={!!formErrors.TelefonMobil}
                helperText={formErrors.TelefonMobil}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              {/* ✅ Enhanced Data Nasterii field with age calculation */}
              <TextField
                label="Data Nașterii"
                type="date"
                value={
                  formData.DataNasterii
                    ? new Date(formData.DataNasterii)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  console.log("📅 Birth date changing to:", e.target.value);
                  handleFormFieldChange("DataNasterii", e.target.value);
                }}
                disabled={dialogMode === "view"}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                helperText={
                  formData.DataNasterii && dialogMode !== "view"
                    ? `Vârsta calculată automat: ${formData.Varsta || 0} ani`
                    : "Vârsta se calculează automat"
                }
              />

              {/* ✅ Enhanced Varsta field - read-only when birth date exists */}
              <TextField
                label="Vârsta"
                type="number"
                value={formData.Varsta || ""}
                onChange={(e) => {
                  // Only allow manual age input if no birth date is set
                  if (!formData.DataNasterii) {
                    handleFormFieldChange(
                      "Varsta",
                      parseInt(e.target.value) || 0,
                    );
                  }
                }}
                disabled={
                  dialogMode === "view" || !!formData.DataNasterii // Disable if birth date exists
                }
                fullWidth
                variant="outlined"
                inputProps={{ min: 0, max: 120 }}
                helperText={
                  formData.DataNasterii && dialogMode !== "view"
                    ? "Calculat automat din data nașterii"
                    : "Introduceți manual sau setați data nașterii"
                }
                InputProps={{
                  endAdornment: formData.DataNasterii && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "success.main",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      🎂 Auto
                    </Box>
                  ),
                }}
              />
            </Box>

            <TextField
              label="Localitatea"
              value={formData.LocalitateAdr || ""}
              onChange={(e) =>
                handleFormFieldChange("LocalitateAdr", e.target.value)
              }
              disabled={dialogMode === "view"}
              fullWidth
              variant="outlined"
            />

            <TextField
              label="Strada"
              value={formData.StradaAdr || ""}
              onChange={(e) =>
                handleFormFieldChange("StradaAdr", e.target.value)
              }
              disabled={dialogMode === "view"}
              fullWidth
              variant="outlined"
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Numărul"
                value={formData.NrAdr || ""}
                onChange={(e) => handleFormFieldChange("NrAdr", e.target.value)}
                disabled={dialogMode === "view"}
                variant="outlined"
                sx={{ flex: 1 }}
              />

              <TextField
                label="Bloc"
                value={formData.BlocAdr || ""}
                onChange={(e) =>
                  handleFormFieldChange("BlocAdr", e.target.value)
                }
                disabled={dialogMode === "view"}
                variant="outlined"
                sx={{ flex: 1 }}
              />

              <TextField
                label="Scara"
                value={formData.ScaraAdr || ""}
                onChange={(e) =>
                  handleFormFieldChange("ScaraAdr", e.target.value)
                }
                disabled={dialogMode === "view"}
                variant="outlined"
                sx={{ flex: 1 }}
              />

              <TextField
                label="Etaj"
                value={formData.EtajAdr || ""}
                onChange={(e) =>
                  handleFormFieldChange("EtajAdr", e.target.value)
                }
                disabled={dialogMode === "view"}
                variant="outlined"
                sx={{ flex: 1 }}
              />

              <TextField
                label="Apartament"
                value={formData.ApartamentAdr || ""}
                onChange={(e) =>
                  handleFormFieldChange("ApartamentAdr", e.target.value)
                }
                disabled={dialogMode === "view"}
                variant="outlined"
                sx={{ flex: 1 }}
              />
            </Box>

            <TextField
              label="Alergii"
              value={formData.alergii || ""}
              onChange={(e) => handleFormFieldChange("alergii", e.target.value)}
              disabled={dialogMode === "view"}
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              placeholder="Alergii cunoscute (opțional)..."
            />

            <TextField
              label="Observații"
              value={formData.Observatii || ""}
              onChange={(e) =>
                handleFormFieldChange("Observatii", e.target.value)
              }
              disabled={dialogMode === "view"}
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              placeholder="Notițe sau observații despre pacient..."
            />

            {dialogMode === "view" && selectedEmployee && (
              <>
                <TextField
                  label="Rating"
                  value={selectedEmployee.rating || 1}
                  disabled
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {Array.from({ length: 5 }, (_, index) => (
                          <span key={index}>
                            {index < (selectedEmployee.rating || 0)
                              ? "⭐"
                              : "☆"}
                          </span>
                        ))}
                      </Box>
                    ),
                  }}
                />

                <TextField
                  label="Status"
                  value={selectedEmployee.isActive ? "Activ" : "Inactiv"}
                  disabled
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <Chip
                        label={selectedEmployee.isActive ? "Activ" : "Inactiv"}
                        color={
                          selectedEmployee.isActive ? "success" : "default"
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    ),
                  }}
                />

                {selectedEmployee.diagnostice &&
                  selectedEmployee.diagnostice.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Diagnostice:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {selectedEmployee.diagnostice.map(
                          (diagnostic, index) => (
                            <Chip
                              key={index}
                              label={diagnostic}
                              variant="outlined"
                              size="small"
                              color="primary"
                            />
                          ),
                        )}
                      </Box>
                    </Box>
                  )}
              </>
            )}

            {dialogMode !== "view" && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Status:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label="Activ"
                    color={formData.isActive ? "success" : "default"}
                    variant={formData.isActive ? "filled" : "outlined"}
                    clickable
                    onClick={() => handleFormFieldChange("isActive", true)}
                  />
                  <Chip
                    label="Inactiv"
                    color={!formData.isActive ? "error" : "default"}
                    variant={!formData.isActive ? "filled" : "outlined"}
                    clickable
                    onClick={() => handleFormFieldChange("isActive", false)}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{ p: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={loading}
          >
            {dialogMode === "view" ? "Închide" : "Anulează"}
          </Button>

          {dialogMode !== "view" && (
            <Button
              variant="contained"
              onClick={() => handleSave(dialogMode === "edit")}
              disabled={loading}
              sx={{
                minWidth: 140,
                fontWeight: 600,
              }}
            >
              {loading
                ? "Se salvează..."
                : dialogMode === "edit"
                  ? "Salvează Modificările"
                  : "Adaugă Pacient"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Consultation Dialog */}
      <Dialog
        open={consultationDialogOpen}
        onClose={handleCloseConsultations}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, minHeight: "80vh" },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            fontWeight: 600,
            fontSize: "1.25rem",
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          Consultatii - {selectedPatientForConsultation?.NumePrenume}
        </DialogTitle>

        <DialogContent sx={{ pt: 3, marginTop: 2 }}>
          {selectedPatientForConsultation && (
            <ConsultationList patientId={selectedPatientForConsultation.id} />
          )}
        </DialogContent>

        <DialogActions
          sx={{ p: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button onClick={handleCloseConsultations} variant="outlined">
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SampleDataGrid;
